import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import { useToast } from '../hooks/use-toast';
import { Plus, FileText, Trash2, Edit, Download, Eye, EyeOff } from 'lucide-react';

const AdminDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', category: 'brochure', file_url: '', file_type: 'pdf', language: 'en', is_public: true
  });
  const { toast } = useToast();

  const loadDocuments = useCallback(async () => {
    try {
      const response = await adminService.getDocuments();
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load documents', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminService.updateDocument(editing.id, formData);
        toast({ title: 'Updated', description: 'Document updated' });
      } else {
        await adminService.createDocument(formData);
        toast({ title: 'Created', description: 'Document added' });
      }
      setShowForm(false);
      setEditing(null);
      setFormData({ title: '', description: '', category: 'brochure', file_url: '', file_type: 'pdf', language: 'en', is_public: true });
      loadDocuments();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save document', variant: 'destructive' });
    }
  };

  const handleEdit = (doc) => {
    setEditing(doc);
    setFormData({
      title: doc.title, description: doc.description || '', category: doc.category,
      file_url: doc.file_url, file_type: doc.file_type, language: doc.language, is_public: doc.is_public
    });
    setShowForm(true);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await adminService.deleteDocument(docId);
      toast({ title: 'Deleted', description: 'Document removed' });
      loadDocuments();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const CATEGORIES = ['brochure', 'site_plan', 'specifications', 'legal', 'pricing', 'general'];

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900" data-testid="admin-docs-title">Document Center</h2>
          <p className="text-gray-600 mt-2">Manage downloadable documents for the public site</p>
        </div>
        <button
          onClick={() => { setEditing(null); setFormData({ title: '', description: '', category: 'brochure', file_url: '', file_type: 'pdf', language: 'en', is_public: true }); setShowForm(true); }}
          data-testid="add-document-btn"
          className="flex items-center gap-2 bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors"
        >
          <Plus className="w-5 h-5" /> Add Document
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : documents.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
          No documents yet. Add your first document!
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full" data-testid="documents-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lang</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Downloads</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visible</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50" data-testid={`doc-row-${doc.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                        {doc.description && <p className="text-xs text-gray-500 truncate max-w-xs">{doc.description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">{doc.category}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{doc.language.toUpperCase()}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Download className="w-3.5 h-3.5" /> {doc.download_count}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {doc.is_public ? <Eye className="w-4 h-4 text-emerald-500" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(doc)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full" data-testid="document-form-modal">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <h3 className="text-xl font-bold text-gray-900">{editing ? 'Edit Document' : 'Add Document'}</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                  <select value={formData.language} onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="zh">Chinese</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File URL *</label>
                <input type="url" required value={formData.file_url} onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                  placeholder="https://..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent" />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_public" checked={formData.is_public} onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })} />
                <label htmlFor="is_public" className="text-sm text-gray-700">Visible on public site</label>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-amber-600 text-white py-2.5 rounded-lg font-semibold hover:bg-amber-700">
                  {editing ? 'Update' : 'Add'} Document
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="flex-1 bg-gray-200 text-gray-800 py-2.5 rounded-lg font-semibold hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDocuments;
